import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AppHeader } from "../components/AppHeader";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Wallet, ArrowUpCircle, ShoppingBag, Receipt } from "lucide-react";
import { StatusBadge } from "../components/StatusBadge";

export default function Dashboard() {
  const { user, loading, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [topups, setTopups] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user || user === false || user.role === "admin") return;
    (async () => {
      try {
        const [o, t] = await Promise.all([api.get("/me/orders"), api.get("/me/topups")]);
        setOrders(o.data);
        setTopups(t.data);
        if (refresh) refresh();
      } catch {}
      setLoaded(true);
    })();
  }, [user, refresh]);

  if (loading) return null;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-violet-400 mb-2">Dashboard</div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Hi, {user.name || "Customer"}</h1>
        </div>

        {/* Stats */}
        <div className="grid gap-3 sm:grid-cols-3 mb-8">
          <div className="rounded-2xl bg-gradient-to-br from-violet-600/20 to-violet-900/10 border border-violet-500/25 p-5 relative overflow-hidden">
            <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-violet-500/20 blur-2xl" />
            <div className="text-[10px] uppercase tracking-widest text-violet-300 mb-1">Baki Wallet</div>
            <div className="text-3xl font-bold font-heading" data-testid="dashboard-balance">
              RM {Number(user.balance || 0).toFixed(2)}
            </div>
            <Link to="/topup" className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500">
              <ArrowUpCircle className="w-3.5 h-3.5" /> Top Up
            </Link>
          </div>
          <div className="rounded-2xl bg-[#0f0f12] border border-white/5 p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Jumlah Order</div>
            <div className="text-3xl font-bold font-heading">{orders.length}</div>
          </div>
          <div className="rounded-2xl bg-[#0f0f12] border border-white/5 p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Total Top Up</div>
            <div className="text-3xl font-bold font-heading">{topups.length}</div>
          </div>
        </div>

        <Tabs defaultValue="orders">
          <TabsList className="bg-[#0f0f12] border border-white/5">
            <TabsTrigger value="orders" data-testid="tab-my-orders"><ShoppingBag className="w-3.5 h-3.5 mr-1.5" />Orders</TabsTrigger>
            <TabsTrigger value="topups" data-testid="tab-my-topups"><Receipt className="w-3.5 h-3.5 mr-1.5" />Top Up History</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-4">
            <div className="rounded-2xl bg-[#0f0f12] border border-white/5 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Servis</TableHead>
                      <TableHead className="text-zinc-500">Target</TableHead>
                      <TableHead className="text-zinc-500">Qty</TableHead>
                      <TableHead className="text-zinc-500">Kos</TableHead>
                      <TableHead className="text-zinc-500">Status</TableHead>
                      <TableHead className="text-zinc-500">Provider ID</TableHead>
                      <TableHead className="text-zinc-500">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((o) => (
                      <TableRow key={o.id} className="border-white/5" data-testid={`my-order-${o.id}`}>
                        <TableCell className="text-white">
                          <span className="font-mono text-violet-400 mr-1">[{o.service_code}]</span>
                          {o.service_name}
                        </TableCell>
                        <TableCell className="text-zinc-400 max-w-[220px] truncate" title={o.target_link}>{o.target_link}</TableCell>
                        <TableCell className="text-zinc-300 font-mono">{o.quantity}</TableCell>
                        <TableCell className="text-zinc-300 font-mono">RM {Number(o.cost).toFixed(2)}</TableCell>
                        <TableCell><StatusBadge status={o.status} /></TableCell>
                        <TableCell className="text-zinc-400 font-mono text-xs">{o.provider_order_id || "-"}</TableCell>
                        <TableCell className="text-zinc-500 text-xs">{new Date(o.created_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                    {loaded && orders.length === 0 && (
                      <TableRow className="border-white/5"><TableCell colSpan={7} className="text-center text-zinc-500 py-8">Belum ada order</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="topups" className="mt-4">
            <div className="rounded-2xl bg-[#0f0f12] border border-white/5 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Amount</TableHead>
                      <TableHead className="text-zinc-500">Nama</TableHead>
                      <TableHead className="text-zinc-500">Status</TableHead>
                      <TableHead className="text-zinc-500">Nota</TableHead>
                      <TableHead className="text-zinc-500">Tarikh</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topups.map((t) => (
                      <TableRow key={t.id} className="border-white/5" data-testid={`my-topup-${t.id}`}>
                        <TableCell className="text-white font-mono">RM {Number(t.amount).toFixed(2)}</TableCell>
                        <TableCell className="text-zinc-300">{t.name}</TableCell>
                        <TableCell><StatusBadge status={t.status} /></TableCell>
                        <TableCell className="text-zinc-400 text-xs">{t.admin_note || "-"}</TableCell>
                        <TableCell className="text-zinc-500 text-xs">{new Date(t.created_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                    {loaded && topups.length === 0 && (
                      <TableRow className="border-white/5"><TableCell colSpan={5} className="text-center text-zinc-500 py-8">Belum ada top up</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
